package com.solevo.dist.viewmodels

import com.solevo.dist.application.models.Invoice
import com.solevo.dist.application.models.InvoiceLines
import com.solevo.dist.application.models.Product
import com.solevo.dist.application.models.ProductItem
import com.solevo.dist.application.models.Transaction
import com.solevo.dist.application.models.toProductItem
import com.solevo.dist.application.models.User
import com.solevo.dist.network.api.user.UserRemoteSource
import com.solevo.dist.repository.ApplicationStatementRepository
import com.solevo.dist.util.DateFormatter
import com.solevo.dist.util.AmountFormatter
import com.solevo.dist.util.StatusMapper
import com.solevo.dist.util.StorageUrlResolver
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.launch
import org.koin.mp.KoinPlatform.getKoin

data class TransactionDetailsUi(
    val id: String = "",
    val type: Transaction.Type = Transaction.Type.Payment,
    val totalAmountText: String = "",
    val createdText: String = "",
    val dueDateText: String? = null,
    val docUrl: String? = null,
    val statusLabel: String = "",
    val statusIcon: String? = null,
    val statusColor: com.varabyte.kobweb.compose.ui.graphics.Color = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(36,41,47),
    val statusColorHex: String = "#24292F",
    val paidText: String = "",
    val remainingText: String = "",
    val items: List<ProductItem> = emptyList()
)

class TransactionDetailsViewModel(private val transactionId: String) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val repo: ApplicationStatementRepository = getKoin().get()
    private val userRemoteSource: UserRemoteSource = getKoin().get()

    private val _state = MutableStateFlow(TransactionDetailsUi())
    val state: StateFlow<TransactionDetailsUi> = _state.asStateFlow()

    @OptIn(ExperimentalCoroutinesApi::class)
    fun start() {
        scope.launch {
            userRemoteSource.currentUser()
                .filterNotNull()
                .flatMapLatest { user ->
                    User.updateCustomerId(user.customerId)
                    repo.getTransaction(transactionId)
                }
                .catch { e ->
                    e.printStackTrace()
                    throw e
                }
                .filterNotNull()
                .onEach { tx ->
                    _state.value = buildBaseUi(tx)
                }
                .flatMapLatest { tx ->
                    if (tx.type is Transaction.Type.Invoice) {
                        getInvoiceItemsFlow(tx)
                    } else {
                        flowOf(Pair(tx, emptyList<ProductItem>()))
                    }
                }
                .catch { e ->
                    e.printStackTrace()
                }
                .collectLatest { (tx, items) ->
                    _state.value = _state.value.copy(items = items)

                    if (_state.value.docUrl.isNullOrBlank()) {
                        val invoiceId = tx.invoiceId?.takeIf { it.isNotBlank() } ?: tx.invoiceNumber?.takeIf { it.isNotBlank() }
                        if (invoiceId != null) {
                            repo.getInvoice(invoiceId).collectLatest { invoice ->
                                val resolved = StorageUrlResolver.resolveDocUrl(invoice?.docMetadata)
                                if (!resolved.isNullOrBlank()) {
                                    _state.value = _state.value.copy(docUrl = resolved)
                                }
                            }
                        }
                    }
                }
        }
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    private fun getInvoiceItemsFlow(tx: Transaction): kotlinx.coroutines.flow.Flow<Pair<Transaction, List<ProductItem>>> {
        val invoiceId = tx.invoiceId?.takeIf { it.isNotBlank() } ?: tx.invoiceNumber?.takeIf { it.isNotBlank() }
        
        return if (invoiceId != null && invoiceId.isNotBlank()) {
            repo.getInvoice(invoiceId)
                .catch { e ->
                    e.printStackTrace()
                    throw e
                }
                .flatMapLatest { invoice ->
                    val productIds = invoice?.invoiceLines?.mapNotNull { it.productId }.orEmpty()
                    
                    if (productIds.isNotEmpty()) {
                        repo.getProducts(productIds)
                            .catch { e ->
                                e.printStackTrace()
                                throw e
                            }
                            .map { products ->
                                mapInvoiceItems(invoice?.invoiceLines.orEmpty(), products)
                            }
                    } else {
                        flowOf(emptyList<ProductItem>())
                    }
                }
                .map { items -> Pair(tx, items) }
        } else {
            flowOf(Pair(tx, emptyList<ProductItem>()))
        }
    }

    private fun buildBaseUi(tx: Transaction): TransactionDetailsUi {
        val isInvoice = tx.type is Transaction.Type.Invoice
        val statusInfo = if (isInvoice) {
            StatusMapper.mapInvoiceStatus(tx.invoiceStatus ?: com.solevo.dist.application.models.InvoiceStatus.Closed)
        } else {
            StatusMapper.mapTransactionStatus(tx.status)
        }
        
        // Convert StatusInfo to Color for UI
        val statusColor = when (statusInfo.colorHex) {
            "#DD3636" -> com.varabyte.kobweb.compose.ui.graphics.Color.rgb(221, 54, 54)
            "#B17700" -> com.varabyte.kobweb.compose.ui.graphics.Color.rgb(177, 119, 0)
            "#01A36C" -> com.varabyte.kobweb.compose.ui.graphics.Color.rgb(1, 163, 108)
            "#6366F1" -> com.varabyte.kobweb.compose.ui.graphics.Color.rgb(99, 102, 241)
            else -> com.varabyte.kobweb.compose.ui.graphics.Color.rgb(36, 41, 47)
        }

        return TransactionDetailsUi(
            id = tx.voucherId ?: tx.transactionId,
            type = tx.type,
            totalAmountText = AmountFormatter.formatAmountWithCurrency(kotlin.math.abs(tx.originalAmount)),
            createdText = DateFormatter.formatAsDayMonthYear(tx.date),
            dueDateText = DateFormatter.formatAsDayMonthYear(tx.invoiceDueDate).takeIf { isInvoice },
            statusLabel = statusInfo.label,
            statusIcon = statusInfo.iconSrc,
            statusColor = statusColor,
            statusColorHex = statusInfo.colorHex,
            paidText = AmountFormatter.formatAmountWithCurrency(kotlin.math.abs(tx.amount)),
            remainingText = AmountFormatter.formatAmountWithCurrency(kotlin.math.abs(tx.remainingAmount)),
            items = emptyList()
        )
    }

    private fun mapInvoiceItems(lines: List<InvoiceLines>, products: List<Product>): List<ProductItem> {
        return lines.map { line ->
            val product = products.firstOrNull { it.productId == line.productId }
            line.toProductItem(product?.getProductName() ?: line.description.orEmpty())
        }.sortedBy { it.lineNumber.toInt() }
    }
}


